import { useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import Button from './Button';

export default function SnapshotChangeModal({
  isOpen,
  title,
  message,
  confirmLabel,
  variant = 'primary',
  busy = false,
  onConfirm,
  onCancel,
}: {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  variant?: 'danger' | 'primary';
  busy?: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [visible, setVisible] = useState(false);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (isOpen) {
      setReason('');
      requestAnimationFrame(() => setVisible(true));
      cancelRef.current?.focus();
    } else {
      setVisible(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 transition-opacity duration-200 ${visible ? 'opacity-100' : 'opacity-0'}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="snapshot-change-title"
      onClick={onCancel}
    >
      <div
        className={`bg-white rounded-2xl w-full max-w-md shadow-xl transition-all duration-200 ${visible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <AlertTriangle className="mx-auto text-gold mb-3" size={36} />
          <h3 id="snapshot-change-title" className="text-lg font-semibold text-navy text-center mb-2">
            {title}
          </h3>
          <p className="text-sm text-gray-600 text-center">{message}</p>
          <label className="block mt-4 text-sm font-medium text-navy">
            Reason for your facilitator (optional)
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="e.g. I submitted the wrong month"
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-navy"
            />
          </label>
        </div>
        <div className="flex gap-3 p-4 border-t border-gray-100">
          <Button
            ref={cancelRef}
            variant="ghost"
            className="flex-1"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            variant={variant}
            className="flex-1"
            onClick={() => onConfirm(reason.trim())}
            disabled={busy}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
